import { ReactNode } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
} from '@mui/material';
import CloseButton from '@ui/CloseButton';

type FormDialogProps = {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  /** Body: confirm text now, a form later (DEV-3361 grows this in place). */
  children?: ReactNode;
  /** Footer buttons, typically Cancel + a primary action. */
  actions?: ReactNode;
  maxWidth?: DialogProps['maxWidth'];
};

/**
 * Reusable form-capable dialog: MUI {@link Dialog} with a titled header
 * ({@link CloseButton}), a content slot for text or a form, and an actions
 * footer. Built to later carry the import mapping form / scope selector;
 * used standalone as a confirm where only `title` + `children` + `actions`
 * are supplied.
 */
export const FormDialog = ({
  open,
  title,
  onClose,
  children,
  actions,
  maxWidth = 'sm',
}: FormDialogProps) => (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth={maxWidth}
    closeAfterTransition
    // The dialog is DOM-portaled but stays a React child of whatever rendered
    // it, so synthetic events still bubble up the React tree. Stop contextmenu
    // here so a right-click inside the modal never triggers a parent row's
    // context menu (e.g. csv-import's "Skip row").
    onContextMenu={(e) => e.stopPropagation()}
  >
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <CloseButton onClick={onClose} />
    </Box>
    {children != null && <DialogContent>{children}</DialogContent>}
    {actions != null && (
      // MUI pads actions with 8px; the dialog needs a wider inset than that.
      <DialogActions sx={{ padding: (theme) => theme.spacing(3, 6, 6) }}>
        {actions}
      </DialogActions>
    )}
  </Dialog>
);
